import { useCallback, useEffect, useState } from 'react';
import { fetchUserDetail, isNotFoundError } from '../services/userService';
import type { UserDetail } from '../services/types';

/** Zustand der geladenen Benutzer-Stammdaten. */
export interface UserDetailState {
  /** Stammdaten oder null (kein Benutzer gewaehlt, laedt noch, Fehler). */
  readonly data: UserDetail | null;
  /** true, waehrend ein Request laeuft. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null. */
  readonly error: string | null;
  /**
   * true, wenn der Benutzer nicht existiert (HTTP 404, z. B. Deep-Link
   * auf eine geloeschte ID). Die Seite blendet dann alles aus, was sich
   * auf diesen Benutzer bezieht (z. B. die Vertragsliste).
   */
  readonly notFound: boolean;
  /**
   * Uebernimmt einen vom Backend bestaetigten Office-Export-Wert in die
   * geladenen Stammdaten, OHNE einen neuen Request auszuloesen. Wirkt nur,
   * wenn aktuell Stammdaten DIESES Benutzers geladen sind (Stale-Guard) —
   * sonst ein no-op.
   */
  applyOfficeExport(enabled: boolean): void;
}

/** Fehlermeldung fuer fehlgeschlagene Detail-Requests. */
const LOAD_ERROR_MESSAGE = 'Die Benutzerdaten konnten nicht geladen werden.';

/** Fehlermeldung fuer eine unbekannte Benutzer-ID (HTTP 404). */
const NOT_FOUND_MESSAGE = 'Der Benutzer wurde nicht gefunden.';

/** Geladenes Ergebnis samt zugehoeriger Benutzer-ID. */
interface LoadedDetail {
  readonly key: string;
  readonly data: UserDetail | null;
  readonly error: string | null;
  /** true, wenn der Request mit HTTP 404 endete. */
  readonly notFound: boolean;
}

/**
 * Laedt die Stammdaten des gewaehlten Benutzers (kein Caching,
 * Projektstandard). `userId === null` bedeutet: kein Benutzer gewaehlt —
 * es wird nichts geladen und nichts angezeigt.
 *
 * Stale-Guard wie in useTimeSeries: Solange das geladene Ergebnis nicht
 * zur aktuellen ID gehoert, meldet der Hook `loading` und liefert KEINE
 * Daten — sonst stuenden im Detailbereich kurzzeitig die Stammdaten des
 * zuvor gewaehlten Benutzers. Das geladene Ergebnis wird zusaetzlich beim
 * START jedes Requests verworfen (und beim Abwaehlen des Benutzers),
 * damit ein Wechsel A -> B -> A nicht kurz die alten Stammdaten ohne
 * Ladeindikator zeigt.
 *
 * Ein HTTP 404 (Deep-Link auf eine geloeschte/unbekannte ID) erhaelt eine
 * eigene Meldung und wird zusaetzlich als `notFound` gemeldet. Fehler
 * werden still behandelt (KEIN console.error).
 *
 * `applyOfficeExport` erlaubt es aufrufendem Code (useOfficeExportToggle),
 * einen vom Backend bestaetigten Office-Export-Wert lokal zu uebernehmen,
 * ohne die Stammdaten neu zu laden.
 */
export function useUserDetail(userId: string | null): UserDetailState {
  const [loaded, setLoaded] = useState<LoadedDetail | null>(null);

  useEffect(() => {
    setLoaded(null);
    if (userId === null) {
      return undefined;
    }
    const controller = new AbortController();
    fetchUserDetail(userId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({ key: userId, data: response, error: null, notFound: false });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        const notFound = isNotFoundError(error);
        setLoaded({
          key: userId,
          data: null,
          error: notFound ? NOT_FOUND_MESSAGE : LOAD_ERROR_MESSAGE,
          notFound,
        });
      });
    return () => {
      controller.abort();
    };
  }, [userId]);

  const applyOfficeExport = useCallback(
    (enabled: boolean): void => {
      setLoaded((previous) => {
        if (previous === null || previous.key !== userId || previous.data === null) {
          return previous;
        }
        return { ...previous, data: { ...previous.data, officeExportEnabled: enabled } };
      });
    },
    [userId]
  );

  if (userId === null) {
    return { data: null, loading: false, error: null, notFound: false, applyOfficeExport };
  }
  const isCurrent = loaded !== null && loaded.key === userId;
  return {
    data: isCurrent ? loaded.data : null,
    loading: !isCurrent,
    error: isCurrent ? loaded.error : null,
    notFound: isCurrent && loaded.notFound,
    applyOfficeExport,
  };
}

export default useUserDetail;
